import {
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  OnInit,
  computed,
  effect,
  inject,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { AuthService } from '@features/auth/services/auth.service';
import { TourService } from '@core/services/tour.service';
import { NoteService } from '@features/notes/services/note.service';
import { AssistantService } from '../../services/assistant.service';
import { AssistantChatStore } from '../../services/assistant-chat.store';
import { AssistantConfig } from '../../models/assistant.model';
import { AssistantChatComponent } from '../assistant-chat/assistant-chat.component';

/** Below this width the panel covers the screen (assistant-launcher.component.scss). */
const FULL_SCREEN_QUERY = '(max-width: 599.98px)';

/** sessionStorage flag: the demo auto-open already happened in this tab. */
export const EZRI_AUTO_OPENED_KEY = 'ezri.autoOpened';
/**
 * localStorage flag, per user: a registered user has met Ezri (it opened by
 * itself once, or they opened it). A demo is a fresh account every time, so
 * it gets the introduction once per tab instead.
 */
export const ezriIntroducedKey = (userId: string | number) => `ezri.introduced.${userId}`;
/** The tour writes this once it was finished or skipped (TourService.STORAGE_KEY). */
const TOUR_COMPLETED_KEY = 'ez_tour_completed';
/**
 * How long the tour has to stay idle before the demo auto-open. The tour's
 * welcome card appears ~0.8-1.8s after the demo lands (DemoComponent /
 * NavComponent call startIfFirstVisit with delays), so before the tour was
 * ever finished we wait well past that; afterwards a short settle is enough.
 */
export const AUTO_OPEN_SETTLE_MS_TOUR_DONE = 2500;
export const AUTO_OPEN_SETTLE_MS_TOUR_PENDING = 6000;

/**
 * Ezri's floating button and panel, mounted once in the authenticated shell.
 * Renders nothing unless GET /api/assistant/config says enabled.
 */
@Component({
  selector: 'app-assistant-launcher',
  standalone: true,
  imports: [MatIconModule, TranslateModule, AssistantChatComponent],
  templateUrl: './assistant-launcher.component.html',
  styleUrls: ['./assistant-launcher.component.scss'],
})
export class AssistantLauncherComponent implements OnInit {
  private readonly api = inject(AssistantService);
  private readonly store = inject(AssistantChatStore);
  private readonly auth = inject(AuthService);
  private readonly tour = inject(TourService);
  private readonly notes = inject(NoteService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  private readonly launcherRef = viewChild<ElementRef<HTMLButtonElement>>('launcher');

  readonly config = signal<AssistantConfig | null>(null);
  readonly enabled = computed(() => this.config()?.enabled === true);
  readonly receipts = computed(() => this.config()?.receipts === true);
  readonly isDemo = computed(() => !!this.auth.user()?.isDemo);
  readonly open = signal(false);
  /** False for the demo auto-open: no focus steal, no phone keyboard. */
  readonly focusOnOpen = signal(true);
  /** The notes FAB sits in the same corner; when it is on, Ezri sits beside it. */
  readonly besideNotes = this.readNotesEnabled();

  private readonly tourBusy = computed(() =>
    this.tour.isActive() || this.tour.showWelcome() || this.tour.showFinal());

  private autoOpenTimer: ReturnType<typeof setTimeout> | null = null;
  private static autoOpenedInMemory = false;

  constructor() {
    // Ezri opens by itself to introduce itself, only while the tour is idle:
    // once per tab for a demo, once ever for a registered user.
    effect(() => {
      const ready = this.enabled() && !!this.auth.user() && !this.tourBusy() && !this.open();
      untracked(() => this.scheduleAutoOpen(ready));
    });
    this.destroyRef.onDestroy(() => this.clearAutoOpen());

    // The transcript is the signed-in user's: follow the user, not just the first
    // one seen. Read once at init, a user still loading meant the shared "anon"
    // key, and each new demo user in the tab got the previous one's conversation
    // (its links then led to records the new user cannot see; 2026-10-18 on dev).
    effect(() => {
      const userId = this.auth.user()?.id;
      untracked(() => this.store.restore(userId));
    });
  }

  ngOnInit(): void {
    const sub = this.api.getConfig().subscribe({
      next: cfg => this.config.set(cfg),
      error: () => this.config.set(null),
    });
    this.destroyRef.onDestroy(() => sub.unsubscribe());
  }

  openPanel(focus = true): void {
    this.clearAutoOpen();
    this.markAutoOpened();
    this.focusOnOpen.set(focus);
    this.open.set(true);
  }

  closePanel(): void {
    this.open.set(false);
    setTimeout(() => this.launcherRef()?.nativeElement.focus());
  }

  /**
   * A press anywhere outside Ezri folds the panel back into its button, as a
   * popover would. Nothing is lost: the conversation and the unsent text live
   * in the store. Not on phones, where the panel is the whole screen, and not
   * for menus or dialogs Ezri opened (they render in the CDK overlay, outside
   * this host). Focus stays where the user pressed.
   */
  @HostListener('document:pointerdown', ['$event'])
  onDocumentPointerDown(event: PointerEvent): void {
    if (!this.open() || this.isFullScreen()) return;
    const target = event.target as Element | null;
    if (!target || this.host.nativeElement.contains(target)) return;
    if (target.closest?.('.cdk-overlay-container')) return;
    this.open.set(false);
  }

  private isFullScreen(): boolean {
    try { return window.matchMedia(FULL_SCREEN_QUERY).matches; } catch { return false; }
  }

  private scheduleAutoOpen(ready: boolean): void {
    this.clearAutoOpen();
    if (!ready || this.wasAutoOpened()) return;
    const settle = this.tourCompleted() ? AUTO_OPEN_SETTLE_MS_TOUR_DONE : AUTO_OPEN_SETTLE_MS_TOUR_PENDING;
    this.autoOpenTimer = setTimeout(() => {
      this.autoOpenTimer = null;
      if (this.tourBusy() || this.open() || this.wasAutoOpened()) return;
      this.openPanel(false);
    }, settle);
  }

  private clearAutoOpen(): void {
    if (this.autoOpenTimer !== null) {
      clearTimeout(this.autoOpenTimer);
      this.autoOpenTimer = null;
    }
  }

  private wasAutoOpened(): boolean {
    if (AssistantLauncherComponent.autoOpenedInMemory) return true;
    try {
      if (sessionStorage.getItem(EZRI_AUTO_OPENED_KEY) === '1') return true;
      const key = this.introducedKey();
      return key !== null && localStorage.getItem(key) === '1';
    } catch { return false; }
  }

  private markAutoOpened(): void {
    AssistantLauncherComponent.autoOpenedInMemory = true;
    try { sessionStorage.setItem(EZRI_AUTO_OPENED_KEY, '1'); } catch { /* memory flag covers it */ }
    const key = this.introducedKey();
    if (key !== null) {
      try { localStorage.setItem(key, '1'); } catch { /* at worst it introduces itself again */ }
    }
  }

  /** Null for a demo user, whose introduction is per tab. */
  private introducedKey(): string | null {
    const user = this.auth.user();
    return user && !user.isDemo && user.id != null ? ezriIntroducedKey(user.id) : null;
  }

  private tourCompleted(): boolean {
    try { return !!localStorage.getItem(TOUR_COMPLETED_KEY); } catch { return false; }
  }

  private readNotesEnabled(): boolean {
    try { return this.notes.isWidgetEnabled(); } catch { return true; }
  }

  /** Test hook: forget the in-memory auto-open flag. */
  static resetAutoOpenForTests(): void {
    AssistantLauncherComponent.autoOpenedInMemory = false;
  }
}
